import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle, Calendar, Award, Briefcase, Mail, Phone, MapPin, QrCode, FileText, UserPlus, Users, FileUser, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const MobilePowerpass = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/users/${userId}/public-profile/`);
        setUserData(response.data);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  // Filter only valid (non-expired) qualifications
  const getValidCertificates = () => {
    if (!userData?.qualifications) return [];

    const today = new Date();
    return userData.qualifications.filter(qual => {
      if (!qual.expiry_date) return false;
      const expiryDate = new Date(qual.expiry_date);
      return expiryDate >= today;
    });
  };

  const validCertificates = userData ? getValidCertificates() : [];

  // Group certificates by category
  const groupedCertificates = validCertificates.reduce((acc, cert) => {
    const category = cert.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(cert);
    return acc;
  }, {});

  // State for active tab
  const [activeTab, setActiveTab] = useState('');

  // Set first tab as active when certificates load
  useEffect(() => {
    const categories = Object.keys(groupedCertificates);
    if (categories.length > 0 && !activeTab) {
      setActiveTab(categories[0]);
    }
  }, [groupedCertificates, activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black mb-4"></div>
          <p className="text-zinc-600 text-sm">Loading Powerpass...</p>
        </div>
      </div>
    );
  }

  if (error || !userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg p-8 text-center border border-zinc-200 shadow-sm">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-black mb-2">Profile Not Found</h2>
          <p className="text-zinc-600 text-sm mb-6">{error || 'This profile may be private or does not exist.'}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2.5 bg-black hover:bg-zinc-800 text-white rounded-md transition-colors text-sm font-medium"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="min-h-screen">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <img
                src="https://ventro.energy/ventro.png"
                alt="Ventro Logo"
                className="h-8"
              />
            </div>
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <FileUser className="w-6 h-6 text-gray-700" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
          {/* MOBILE LAYOUT - 3 Column Grid */}
          <div className="lg:hidden">
            {/* TOP ROW - 2 Column Grid */}
            <div className="grid grid-cols-[35%_65%] gap-x-3 mb-4">
              {/* Left Column: Photo */}
              <div className="col-span-1 flex flex-col items-center text-center">
                <div className="relative inline-block">
                  <img
                    src={userData.profile_picture || 'https://via.placeholder.com/400'}
                    alt={`${userData.first_name} ${userData.last_name}`}
                    className="w-28 h-28 rounded-full object-cover border-3 border-white shadow-md"
                  />
                  {/* Status indicator - Polish flag style */}
                  <div className="absolute top-1 left-0 w-6 h-6 rounded-full border-[1.5px] border-black" style={{background: 'linear-gradient(to bottom, white 50%, #dc143c 50%)'}}></div>
                  {/* Verified Badge */}
                  <div className="absolute bottom-0 right-0 bg-green-500 rounded-full p-1.5 border-2 border-white shadow-md">
                    <CheckCircle className="w-3.5 h-3.5 text-white" />
                  </div>
                </div>
              </div>

              {/* Right Column: Email and Phone */}
              <div className="col-span-1">
                <div className="space-y-2">
                  {/* Email */}
                  {userData.email && (
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Mail className="w-3.5 h-3.5 text-gray-700 flex-shrink-0" />
                        <p className="text-xs font-semibold text-black">E-mail</p>
                      </div>
                      <a href={`mailto:${userData.email}`} className="text-xs text-black underline break-all line-clamp-2">
                        {userData.email}
                      </a>
                    </div>
                  )}

                  {/* Phone */}
                  {userData.phone_number && (
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Phone className="w-3.5 h-3.5 text-gray-700 flex-shrink-0" />
                        <p className="text-xs font-semibold text-black">Phone</p>
                      </div>
                      <a href={`tel:${userData.phone_number}`} className="text-xs text-black underline">
                        {userData.phone_number}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Name - Between Grid Rows, Full Width */}
            <div className="text-center mb-4">
              <h1 className="text-2xl font-bold text-black break-words leading-tight">
                {userData.first_name} {userData.last_name}
              </h1>
            </div>

            {/* Languages Row - Full Width */}
            {userData.languages && userData.languages.length > 0 && (
              <div className="flex items-center justify-center gap-2 mb-4 w-full">
                <span className="text-base">🌐</span>
                <span className="text-sm font-semibold text-black">Languages:</span>
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  {userData.languages.map((lang, index) => (
                    <span key={index} className="text-sm font-semibold text-black">
                      {lang.flag} {lang.name} - {lang.level}
                      {index < userData.languages.length - 1 && ','}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* WINDA ID Section */}
            <div className="flex justify-center mb-3">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full">
                <QrCode className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                <span className="text-xs font-mono font-semibold text-black whitespace-nowrap">
                  WINDA ID: {userData.winda_id || 'N/A'}
                </span>
              </div>
            </div>

            {/* Action Buttons Row */}
            <div className="flex gap-3 px-4 mb-6">
              <button className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-colors text-xs">
                <Phone className="w-3.5 h-3.5" />
                Add Contact
              </button>
              <button className="flex-1 bg-black hover:bg-gray-800 text-white font-semibold py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-colors text-xs">
                <Users className="w-3.5 h-3.5" />
                Add to Network
              </button>
            </div>

            {/* Position Badge - Below Grid */}
            {userData.position && (
              <div className="bg-gray-100 rounded-xl px-4 py-2 inline-block mb-4">
                <p className="text-sm font-bold text-black uppercase">{userData.position}</p>
              </div>
            )}
          </div>

          {/* DESKTOP LAYOUT - Hidden on mobile, visible on desktop */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-6">
            {/* Left Column: Profile Photo & Name */}
            <div className="lg:col-span-3 flex flex-col items-center text-center">
              <div className="relative inline-block mb-3">
                <img
                  src={userData.profile_picture || 'https://via.placeholder.com/400'}
                  alt={`${userData.first_name} ${userData.last_name}`}
                  className="w-32 h-32 rounded-full object-cover border-3 border-white shadow-md"
                />
                {/* Status indicator - Polish flag style */}
                <div className="absolute top-1 left-0 w-7 h-7 rounded-full border-[1.5px] border-black" style={{background: 'linear-gradient(to bottom, white 50%, #dc143c 50%)'}}></div>
                {/* Verified Badge */}
                <div className="absolute bottom-1 right-1 bg-green-500 rounded-full p-1.5 border-3 border-white shadow-md">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              </div>

              <h1 className="text-2xl font-bold text-black mb-2 leading-tight">
                {userData.first_name} {userData.last_name}
              </h1>

              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-center">
                <QrCode className="w-3.5 h-3.5 text-gray-600" />
                <span className="text-sm font-mono font-semibold text-black whitespace-nowrap">
                  WINDA ID: {userData.winda_id || 'N/A'}
                </span>
              </div>
            </div>

            {/* Center Column: Contact Info & Position */}
            <div className="lg:col-span-6 space-y-3">
              {/* Position */}
              {userData.position && (
                <div className="bg-gray-100 rounded-xl px-4 py-2 inline-block">
                  <p className="text-base font-bold text-black uppercase">{userData.position}</p>
                </div>
              )}

              {/* Email */}
              {userData.email && (
                <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-700" />
                  <div>
                    <p className="text-xs font-semibold text-black">E-mail:</p>
                    <a href={`mailto:${userData.email}`} className="text-xs text-black underline">
                      {userData.email}
                    </a>
                  </div>
                </div>
              )}

              {/* Phone */}
              {userData.phone_number && (
                <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-700" />
                  <div>
                    <p className="text-xs font-semibold text-black">Phone:</p>
                    <a href={`tel:${userData.phone_number}`} className="text-xs text-black underline">
                      {userData.phone_number}
                    </a>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-1">
                <button className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-1.5 px-3 rounded-xl flex items-center justify-center gap-1 transition-colors text-xs">
                  <Phone className="w-3 h-3" />
                  Add Contact
                </button>
                <button className="flex-1 bg-black hover:bg-gray-800 text-white font-semibold py-1.5 px-3 rounded-xl flex items-center justify-center gap-1 transition-colors text-xs">
                  <Users className="w-3 h-3" />
                  Add to Network
                </button>
              </div>
            </div>

            {/* Right Column: Languages */}
            <div className="lg:col-span-3">
              {userData.languages && userData.languages.length > 0 && (
                <>
                  <div className="flex items-center gap-1.5 mb-3">
                    <span className="text-xl">🌐</span>
                    <h3 className="text-base font-bold text-black">Languages</h3>
                  </div>
                  <div className="space-y-2">
                    {userData.languages.map((lang, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-2xl">{lang.flag}</span>
                        <div>
                          <p className="text-sm font-semibold text-black">{lang.name} - {lang.level}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Category Pills - Mobile Only */}
          <div className="lg:hidden border-t border-gray-200 pt-3 md:pt-4 -mx-4 px-4">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" style={{WebkitOverflowScrolling: 'touch'}}>
              {Object.keys(groupedCertificates).map((category, index) => {
                const colors = [
                  'bg-purple-200 text-purple-900 hover:bg-purple-300',
                  'bg-green-200 text-green-900 hover:bg-green-300',
                  'bg-cyan-200 text-cyan-900 hover:bg-cyan-300',
                  'bg-yellow-200 text-yellow-900 hover:bg-yellow-300'
                ];
                const color = colors[index % colors.length];

                return (
                  <button
                    key={category}
                    onClick={() => setActiveTab(category)}
                    className={`px-4 py-1.5 text-xs font-bold whitespace-nowrap rounded-full transition-all flex-shrink-0 ${
                      activeTab === category ? color : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {category.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Valid Certifications Section - Mobile (Single Card with Active Tab) */}
          {validCertificates.length > 0 && (
            <div className="lg:hidden bg-white rounded-xl border border-gray-200 shadow-sm p-3">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <h2 className="text-base font-bold text-black">Valid Certifications</h2>
                  <p className="text-xs text-gray-500">{validCertificates.length} active certificate{validCertificates.length !== 1 ? 's' : ''}</p>
                </div>
              </div>

              {/* Active Category Header */}
              {activeTab && (
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-black">{activeTab.toUpperCase()}</h3>
                  <span className="text-sm font-bold text-black">{groupedCertificates[activeTab]?.length || 0}</span>
                </div>
              )}

              {/* Certificates List */}
              <div className="space-y-2">
                {groupedCertificates[activeTab]?.map((cert, index) => (
                  <div
                    key={index}
                    className="bg-green-50 border-2 border-green-200 rounded-lg p-3 transition-all hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border-2 border-green-200">
                          <Award className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-black text-xs mb-1 break-words">
                            {cert.qualification_type_name || cert.name}
                          </h3>
                          {cert.expiry_date && (
                            <div className="flex items-center gap-1 text-xs text-green-700">
                              <Calendar className="w-3 h-3 flex-shrink-0" />
                              <span>Valid until {new Date(cert.expiry_date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="px-2.5 py-1 bg-green-500 rounded-full">
                          <span className="text-xs font-bold text-white">VALID</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Valid Certifications Section - Desktop (All Categories as Separate Cards) */}
          {validCertificates.length > 0 && (
            <div className="hidden lg:block">
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-7 h-7 text-green-600 flex-shrink-0" />
                <div>
                  <h2 className="text-lg font-bold text-black">Valid Certifications</h2>
                  <p className="text-xs text-gray-500">{validCertificates.length} active certificate{validCertificates.length !== 1 ? 's' : ''}</p>
                </div>
              </div>

              {/* Grid of Category Cards */}
              <div className="grid grid-cols-2 gap-4">
                {Object.keys(groupedCertificates).map((category, index) => {
                  const colors = [
                    { bg: 'bg-purple-50', text: 'text-purple-900', badge: 'bg-purple-200' },
                    { bg: 'bg-green-50', text: 'text-green-900', badge: 'bg-green-200' },
                    { bg: 'bg-cyan-50', text: 'text-cyan-900', badge: 'bg-cyan-200' },
                    { bg: 'bg-yellow-50', text: 'text-yellow-900', badge: 'bg-yellow-200' }
                  ];
                  const colorScheme = colors[index % colors.length];

                  return (
                    <div
                      key={category}
                      className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm p-4"
                    >
                      {/* Category Header */}
                      <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                        <h3 className={`text-base font-bold ${colorScheme.text}`}>{category.toUpperCase()}</h3>
                        <span className={`px-3 py-1 ${colorScheme.badge} rounded-full text-xs font-bold ${colorScheme.text}`}>
                          {groupedCertificates[category]?.length || 0}
                        </span>
                      </div>

                      {/* Certificates List */}
                      <div className="space-y-2.5">
                        {groupedCertificates[category]?.map((cert, certIndex) => (
                          <div
                            key={certIndex}
                            className={`${colorScheme.bg} border-2 border-gray-200 rounded-xl p-3.5 transition-all hover:shadow-md`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border-2 border-gray-200">
                                  <Award className={`w-5 h-5 ${colorScheme.text}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-bold text-black text-sm mb-1 break-words">
                                    {cert.qualification_type_name || cert.name}
                                  </h4>
                                  {cert.expiry_date && (
                                    <div className={`flex items-center gap-1 text-xs ${colorScheme.text}`}>
                                      <Calendar className="w-3 h-3 flex-shrink-0" />
                                      <span>Valid until {new Date(cert.expiry_date).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      })}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex-shrink-0">
                                <div className="px-3 py-1 bg-green-500 rounded-full">
                                  <span className="text-xs font-bold text-white">VALID</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {validCertificates.length === 0 && (
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm text-center">
              <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <Award className="w-8 h-8 text-yellow-500" />
              </div>
              <h3 className="text-black font-semibold mb-1">No Valid Certifications</h3>
              <p className="text-sm text-gray-500">This user has no active certifications at the moment</p>
            </div>
          )}

          {/* Call to Action Banner - Mobile only */}
          <div className="lg:hidden bg-black rounded-xl p-6 text-center mt-8">
            <h3 className="text-lg font-bold text-white mb-2">
              Join Ventro Platform
            </h3>
            <p className="text-gray-300 text-sm mb-4">
              Connect with professionals and manage your wind energy operations
            </p>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors font-semibold"
            >
              Get Started
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>

          {/* Footer */}
          <div className="text-center py-8 border-t border-gray-200 mt-8">
            <p className="text-sm text-gray-500">
              Powered by Ventro Energy © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>

      {/* Custom Scrollbar Hide */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default MobilePowerpass;
